/**
 * Owner: Masa Bayu
 * File: MotionPreferencePanel.tsx
 * Date:Time: 2026-09-16 18:53
 * Description: T8-E6 Motion preference UI — reduced-motion Flip fallback.
 */

import { useId, useState } from 'react';
import {
  enableDemoReducedMotion,
  getDemoAdvancedStatus,
  getDemoMotionPreference,
  resetDemoAdvancedStore,
} from './demo-advanced-store';

export interface MotionPreferencePanelProps {
  readonly onNavigate: (path: string) => void;
}

export function MotionPreferencePanel({ onNavigate }: MotionPreferencePanelProps) {
  const statusId = useId();
  const [status, setStatus] = useState(() => getDemoAdvancedStatus());
  const [pref, setPref] = useState(() => getDemoMotionPreference());

  const refresh = (next: string) => {
    setStatus(next);
    setPref(getDemoMotionPreference());
  };

  return (
    <section className="route-panel motion-pref" aria-labelledby="motion-pref-title">
      <h2 id="motion-pref-title">Motion · preference</h2>
      <p>
        Reduced-motion preference sets Flip fallback to instant. Preference does not execute Flip.
      </p>
      <p id={statusId} role="status" aria-live="polite" className="motion-pref__status">
        {status}
        {pref !== null ? ` · fallback ${pref.flipFallback}` : ''}
      </p>
      <div role="group" aria-label="Motion preference actions" className="motion-pref__controls">
        <button
          type="button"
          onClick={() => {
            refresh(enableDemoReducedMotion());
          }}
        >
          Enable reduced motion
        </button>
        <button
          type="button"
          onClick={() => {
            resetDemoAdvancedStore();
            refresh(getDemoAdvancedStatus());
          }}
        >
          Reset demo
        </button>
        <button
          type="button"
          onClick={() => {
            onNavigate('/flip');
          }}
        >
          Flip
        </button>
      </div>
      {pref !== null ? (
        <ul aria-label="Motion preference facts" className="motion-pref__facts">
          <li>{`reducedMotion=${String(pref.reducedMotion)}`}</li>
          <li>{`flipFallback=${pref.flipFallback}`}</li>
          <li>{`executesFlip=${String(pref.executesFlip)}`}</li>
        </ul>
      ) : null}
    </section>
  );
}
